import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import { subscribeToNotifications } from "../../../api/notificationStream";
import {
  NOTIFICATION_STATUS_LABELS,
  getNotificationErrorMessage,
  canRetryNotification,
  notificationAttemptsExhausted,
  latestNotification,
  listNotifications,
  retryNotification,
} from "../../../api/whatsappNotificationsApi";

const STYLES = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  queued: "bg-slate-100 text-slate-600 border-slate-200",
  processing: "bg-blue-50 text-blue-700 border-blue-100",
  accepted: "bg-blue-50 text-blue-700 border-blue-100",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-100",
  read: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed: "bg-rose-50 text-rose-700 border-rose-100",
  uncertain: "bg-amber-50 text-amber-700 border-amber-100",
  skipped: "bg-slate-100 text-slate-500 border-slate-200",
};

const EXPLANATIONS = {
  pending: "Waiting to be sent.",
  queued: "Queued for sending. It has not been sent yet.",
  processing: "Being sent to WhatsApp.",
  accepted: "Accepted by WhatsApp; delivery not yet confirmed.",
  uncertain: "Outcome unknown, so it is not retried automatically. Check WhatsApp before sending again.",
};

export function NotificationBadge({ notification }) {
  if (!notification) return null;
  const status = notification.status;
  return (
    <span
      title={EXPLANATIONS[status] || notification.last_error || undefined}
      className={`inline-block rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${STYLES[status] || STYLES.pending}`}
    >
      WhatsApp: {NOTIFICATION_STATUS_LABELS[status] || status}
    </span>
  );
}

// Describes a notification row returned by a create/send call without claiming it was sent.
export function notificationOutcomeText(notification, subject = "notification") {
  if (!notification) return "";
  const status = notification.status;
  if (status === "failed") return `WhatsApp ${subject} failed${notification.last_error ? `: ${notification.last_error}` : "."}`;
  if (status === "skipped") return `WhatsApp ${subject} was skipped${notification.last_error ? `: ${notification.last_error}` : "."}`;
  if (status === "uncertain") return `WhatsApp ${subject} outcome is uncertain. It will not be retried automatically.`;
  if (status === "delivered" || status === "read") return `WhatsApp ${subject} ${status}.`;
  if (status === "accepted") return `WhatsApp ${subject} accepted by WhatsApp; delivery not yet confirmed.`;
  return `WhatsApp ${subject} queued. It has not been sent yet.`;
}

// Resource-scoped status with refresh and retry for failed rows.
export default function NotificationStatus({ botId, resourceType, resourceId, initial = null, compact = false }) {
  const { can } = useAuth();
  const readPermission = resourceType === "appointment" ? "appointments.read"
    : resourceType === "form_request" ? "consent_requests.read"
      : resourceType === "schedule" ? "schedules.read" : null;
  const retryPermission = resourceType === "appointment" ? "appointments.update"
    : resourceType === "form_request" ? "consent_requests.send"
      : resourceType === "schedule" ? "schedules.update" : null;
  const canRead = Boolean(readPermission && can(readPermission));
  const canRetry = Boolean(retryPermission && can(retryPermission));
  const [row, setRow] = useState(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!canRead) return;
    const requestId = ++requestRef.current;
    setError("");
    setBusy(true);
    try {
      const rows = await listNotifications(botId, { resource_type: resourceType, resource_id: resourceId, limit: 5 });
      if (requestId === requestRef.current) setRow(latestNotification(rows));
    } catch (err) {
      if (requestId === requestRef.current) setError(getNotificationErrorMessage(err, "Could not load WhatsApp status."));
    } finally {
      if (requestId === requestRef.current) setBusy(false);
    }
  }, [botId, resourceType, resourceId, canRead]);

  const eventDriven = resourceType === "appointment";

  // Appointment status is fetched only for the matching booking/cancellation hint.
  useEffect(() => {
    setRow(initial);
    setError("");
    setBusy(false);
    if (!canRead) return undefined;
    const unsubscribe = eventDriven
      ? subscribeToNotifications(String(botId), (event) => {
        if (
          String(event.bot_id) === String(botId) &&
          String(event.resource_id) === String(resourceId) &&
          ["appointment.booked", "appointment.cancelled"].includes(event.type)
        ) load();
      })
      : null;
    if (!eventDriven) load();
    return () => {
      requestRef.current += 1;
      unsubscribe?.();
    };
  }, [load, eventDriven, canRead]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async () => {
    if (!canRetry) return;
    const requestId = ++requestRef.current;
    setError("");
    setBusy(true);
    try {
      const next = await retryNotification(botId, row.id);
      if (requestId === requestRef.current) setRow(next || row);
    } catch (err) {
      if (requestId === requestRef.current) setError(getNotificationErrorMessage(err, "Could not retry the WhatsApp notification."));
    } finally {
      if (requestId === requestRef.current) setBusy(false);
    }
  };

  if (!row && !error) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? "text-[11px]" : "text-xs"}`}>
      <NotificationBadge notification={row} />
      {row?.last_error && ["failed", "skipped", "uncertain"].includes(row.status) && <span className="text-rose-700">{row.last_error}</span>}
      {row?.status === "failed" && !canRetryNotification(row) && <span className="text-slate-500">{notificationAttemptsExhausted(row) ? "No retries left." : "Retry unavailable."}</span>}
      {row && canRetry && canRetryNotification(row) && (
        <button type="button" onClick={retry} disabled={busy} className="rounded-md border border-slate-200 px-2 py-0.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Retry</button>
      )}
      {!eventDriven && <button type="button" onClick={load} disabled={busy} aria-label="Refresh WhatsApp status" title="Refresh WhatsApp status" className="text-slate-400 hover:text-slate-700"><RefreshCw size={12} /></button>}
      {error && <span role="alert" className="text-rose-700">{error}</span>}
    </div>
  );
}
